/**
 * Typed fetch wrapper.
 *
 * Auth is a same-site httpOnly cookie, so every request just needs
 * `credentials: 'include'` — there is no token for the SPA to hold or leak.
 */
import type { ApiError } from '@titrate/shared';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly payload: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** True when the user needs to upgrade or has exhausted an allotment. */
  get isQuotaError() {
    return this.status === 402 || this.status === 429;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : {};

  if (!res.ok) {
    const err = data as ApiError & Record<string, unknown>;
    throw new ApiRequestError(
      res.status,
      err.error ?? 'error',
      err.message ?? `Request failed with status ${res.status}`,
      err,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
